// Upload helper utilities: compress images, persist tag choices, and show progress.
const MAX_DIMENSION = 2400; // Resize larger images down to this bounding box.
const MAX_BATCH = 10; // Matches server-side limit.
const LAST_TAG_IDS_KEY = 'hm_last_tags'; // localStorage key for checkboxes.
const LAST_TAG_TEXT_KEY = 'hm_last_tag_text'; // localStorage key for freeform text.

const setStatus = (statusEl, message) => {
  if (!statusEl) return;
  statusEl.textContent = message || '';
  statusEl.classList.toggle('is-active', Boolean(message));
};

// Read EXIF orientation so we can draw the preview correctly.
const readOrientation = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const view = new DataView(event.target.result);
      if (view.getUint16(0, false) !== 0xffd8) {
        resolve(1);
        return;
      }
      let offset = 2;
      const length = view.byteLength;
      while (offset < length) {
        const marker = view.getUint16(offset, false);
        offset += 2;
        if (marker === 0xffe1) {
          offset += 2;
          if (view.getUint32(offset, false) !== 0x45786966) {
            break;
          }
          offset += 6;
          const little = view.getUint16(offset, false) === 0x4949;
          offset += view.getUint32(offset + 4, little);
          const tags = view.getUint16(offset, little);
          offset += 2;
          for (let i = 0; i < tags; i += 1) {
            const tagOffset = offset + i * 12;
            if (view.getUint16(tagOffset, little) === 0x0112) {
              resolve(view.getUint16(tagOffset + 8, little));
              return;
            }
          }
        } else if ((marker & 0xff00) !== 0xff00) {
          break;
        } else {
          offset += view.getUint16(offset, false);
        }
      }
      resolve(1);
    };
    reader.readAsArrayBuffer(file.slice(0, 64 * 1024));
  });

// Load the image so we can draw/resize it on a canvas.
const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

const getTargetSize = (width, height) => {
  if (width <= MAX_DIMENSION && height <= MAX_DIMENSION) {
    return { width, height };
  }
  // Scale down while maintaining aspect ratio.
  const scale = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
};

// Apply EXIF orientation while drawing the resized image onto a canvas.
const drawOrientedImage = (img, orientation, targetWidth, targetHeight) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const swap = orientation >= 5 && orientation <= 8;
  canvas.width = swap ? targetHeight : targetWidth;
  canvas.height = swap ? targetWidth : targetHeight;

  ctx.save();
  switch (orientation) {
    case 2:
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      break;
    case 3:
      ctx.translate(canvas.width, canvas.height);
      ctx.rotate(Math.PI);
      break;
    case 4:
      ctx.translate(0, canvas.height);
      ctx.scale(1, -1);
      break;
    case 5:
      ctx.rotate(0.5 * Math.PI);
      ctx.scale(1, -1);
      break;
    case 6:
      ctx.rotate(0.5 * Math.PI);
      ctx.translate(0, -canvas.width);
      break;
    case 7:
      ctx.rotate(0.5 * Math.PI);
      ctx.translate(canvas.height, -canvas.width);
      ctx.scale(-1, 1);
      break;
    case 8:
      ctx.rotate(-0.5 * Math.PI);
      ctx.translate(-canvas.height, 0);
      break;
    default:
      break;
  }

  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
  ctx.restore();
  return canvas;
};

// Convert canvas back to a Blob; PNG keeps full quality, others get a mild compression.
const canvasToBlob = (canvas, type, quality = 0.82) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create blob'));
      },
      type,
      quality
    );
  });

// Main optimization pipeline: resize, re-orient, and re-encode.
const optimiseImage = async (file, index, total, statusEl) => {
  setStatus(statusEl, total > 1 ? `Optimizing ${index + 1} of ${total}...` : 'Optimizing image...');
  const orientation = await readOrientation(file);
  const img = await loadImage(file);
  const { width, height } = getTargetSize(img.width, img.height);
  const canvas = drawOrientedImage(img, orientation, width, height);
  const type = ['image/jpeg', 'image/webp', 'image/png'].includes(file.type) ? file.type : 'image/jpeg';
  const quality = type === 'image/png' ? undefined : 0.82;
  const blob = await canvasToBlob(canvas, type, quality);
  const optimized = new File([blob], file.name, { type: blob.type, lastModified: Date.now() });
  URL.revokeObjectURL(img.src);
  return optimized;
};

// Utility: build a DataTransfer object so we can set FileList programmatically.
const dataTransferFromFiles = (files) => {
  if (typeof DataTransfer === 'undefined') return null;
  const dt = new DataTransfer();
  files.forEach((file) => dt.items.add(file));
  return dt;
};

// Read a single file into a data URL for UI previews.
const readFilePreview = (file, callback) => {
  const reader = new FileReader();
  reader.onload = () => callback(reader.result);
  reader.readAsDataURL(file);
};

// Storage helpers wrapped in try/catch to survive Safari/Incognito.
const safeGetLocal = (key, fallback = '') => {
  try {
    return window.localStorage.getItem(key) || fallback;
  } catch (e) {
    return fallback;
  }
};

const safeSetLocal = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    // ignore
  }
};

const safeGetJson = (key) => {
  try {
    return JSON.parse(window.localStorage.getItem(key) || '[]');
  } catch (e) {
    return [];
  }
};

const safeSetJson = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // ignore
  }
};

export const initUploadForm = () => {
  // Only attach listeners on upload pages.
  const form = document.querySelector('.upload-form');
  if (!form) return;

  const dropZone = form.querySelector('[data-drop-zone]');
  const fileInput = form.querySelector('#hm_photos') || form.querySelector('#hm_photo');
  const fileNameEl = form.querySelector('[data-drop-filename]');
  const batchList = form.querySelector('[data-batch-list]');
  const previewEl = form.querySelector('[data-drop-preview]');
  const statusEl = form.querySelector('[data-upload-status]');
  const newTagInput = form.querySelector('#hm_new_tag');
  const tagCheckboxes = form.querySelectorAll('input[name="hm_tags[]"]');
  const progressWrap = form.querySelector('[data-upload-progress-wrapper]');
  const progressBar = form.querySelector('[data-upload-progress]');
  const progressLabel = form.querySelector('[data-upload-progress-label]');
  const submitBtn = form.querySelector('button[type="submit"]');

  if (!dropZone || !fileInput) return;

  // Mirror selected file names in the UI.
  const updateLabel = (files) => {
    if (fileNameEl && files && files.length) {
      fileNameEl.textContent = files.length === 1 ? files[0].name : `${files.length} files selected`;
    } else if (fileNameEl) {
      fileNameEl.textContent = '';
    }
  };

  // Persisted freeform tag text may pre-fill new tag inputs.
  const getSavedTagText = () => safeGetLocal(LAST_TAG_TEXT_KEY, '');

  // Render per-file cards in batch mode so users can set unique metadata.
  const renderBatchCards = (files) => {
    if (!batchList) return;
    batchList.innerHTML = '';
    const savedTagText = getSavedTagText();
    Array.from(files).forEach((file) => {
      const card = document.createElement('div');
      card.className = 'batch-card';

      const preview = document.createElement('div');
      preview.className = 'batch-thumb';
      readFilePreview(file, (result) => {
        preview.style.backgroundImage = `url(${result})`;
      });

      const titleLabel = document.createElement('label');
      titleLabel.textContent = 'Title';
      const titleInput = document.createElement('input');
      titleInput.type = 'text';
      titleInput.name = 'hm_titles[]';
      titleInput.value = file.name.replace(/\.[^/.]+$/, '');

      const tagLabel = document.createElement('label');
      tagLabel.textContent = 'Tags (comma separated)';
      const tagInput = document.createElement('input');
      tagInput.type = 'text';
      tagInput.name = 'hm_taglist[]';
      tagInput.placeholder = 'e.g., travel, portrait';
      tagInput.setAttribute('list', 'hm-tag-suggestions');
      if (savedTagText) {
        tagInput.value = savedTagText;
      }

      const titleWrap = document.createElement('div');
      titleWrap.className = 'batch-field';
      titleWrap.appendChild(titleLabel);
      titleWrap.appendChild(titleInput);

      const tagWrap = document.createElement('div');
      tagWrap.className = 'batch-field';
      tagWrap.appendChild(tagLabel);
      tagWrap.appendChild(tagInput);

      card.appendChild(preview);
      card.appendChild(titleWrap);
      card.appendChild(tagWrap);
      batchList.appendChild(card);
    });
  };

  // Preview single-upload image so the drop zone feels responsive.
  const updatePreview = (file) => {
    if (!previewEl || !file) return;
    readFilePreview(file, (result) => {
      previewEl.style.backgroundImage = `url(${result})`;
      previewEl.setAttribute('aria-hidden', 'false');
    });
  };

  // Resize/optimise the incoming files one by one.
  const processFiles = async (files) => {
    const list = Array.from(files);
    const selected = batchList ? list.slice(0, MAX_BATCH) : [list[0]];
    if (batchList && list.length > MAX_BATCH) {
      setStatus(statusEl, `Using the first ${MAX_BATCH} files (batch limit).`);
    }
    const optimized = [];
    for (let i = 0; i < selected.length; i += 1) {
      try {
        const file = await optimiseImage(selected[i], i, selected.length, statusEl);
        optimized.push(file);
      } catch (e) {
        optimized.push(selected[i]);
      }
    }
    setStatus(statusEl, '');
    return optimized;
  };

  // Apply optimized files back to the hidden file input.
  const assignFiles = async (files) => {
    if (!files || !files.length) return;
    try {
      const optimized = await processFiles(files);
      const dt = dataTransferFromFiles(optimized);
      if (dt) {
        fileInput.files = dt.files;
        updateLabel(dt.files);
      } else {
        updateLabel(optimized);
      }

      if (batchList) {
        renderBatchCards(optimized);
      } else {
        updatePreview(optimized[0]);
      }
    } catch (e) {
      setStatus(statusEl, 'We could not optimize the images. Using originals.');
    }
  };

  // Simple progress helper for the inline bar.
  const setProgress = (value, message) => {
    if (!progressWrap || !progressBar) return;
    progressWrap.setAttribute('aria-hidden', 'false');
    progressWrap.classList.add('is-visible');
    const percent = Math.max(0, Math.min(100, Math.round(value)));
    progressBar.style.width = `${percent}%`;
    progressBar.setAttribute('aria-valuenow', percent.toString());
    if (progressLabel) {
      progressLabel.textContent = message || '';
    }
  };

  // Visually disable the form while the XHR is running.
  const toggleSubmitting = (state) => {
    form.classList.toggle('is-submitting', state);
    if (submitBtn) submitBtn.disabled = state;
  };

  // Persist tag choices to localStorage to speed repeat uploads.
  const saveTagsState = () => {
    if (tagCheckboxes.length) {
      const checked = Array.from(tagCheckboxes)
        .filter((input) => input.checked)
        .map((input) => input.value);
      safeSetJson(LAST_TAG_IDS_KEY, checked);
    }
    if (newTagInput) {
      safeSetLocal(LAST_TAG_TEXT_KEY, newTagInput.value);
    }
  };

  // Restore tags on load so users do not have to re-check common ones.
  const applySavedTags = () => {
    const storedIds = safeGetJson(LAST_TAG_IDS_KEY);
    const anyChecked = Array.from(tagCheckboxes).some((input) => input.checked);
    if (!anyChecked && storedIds.length) {
      Array.from(tagCheckboxes).forEach((input) => {
        if (storedIds.includes(input.value)) {
          input.checked = true;
        }
      });
    }
    if (newTagInput && !newTagInput.value) {
      const savedText = getSavedTagText();
      if (savedText) {
        newTagInput.value = savedText;
      }
    }
  };

  applySavedTags();

  // Proxy clicks/keyboard on the drop zone to the hidden file input.
  dropZone.addEventListener('click', () => fileInput.click());
  dropZone.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      fileInput.click();
    }
  });

  dropZone.addEventListener('dragover', (event) => {
    event.preventDefault();
    dropZone.classList.add('is-dragover');
  });

  // Clean up styling when dragging leaves the area.
  dropZone.addEventListener('dragleave', () => {
    dropZone.classList.remove('is-dragover');
  });

  // Handle drop-to-upload for both single and batch modes.
  dropZone.addEventListener('drop', async (event) => {
    event.preventDefault();
    dropZone.classList.remove('is-dragover');
    if (event.dataTransfer?.files?.length) {
      try {
        await assignFiles(event.dataTransfer.files);
      } catch (e) {
        setStatus(statusEl, 'Upload ready without optimization.');
      }
    }
  });

  // Also respond to traditional file selection.
  fileInput.addEventListener('change', async () => {
    try {
      await assignFiles(fileInput.files);
    } catch (e) {
      setStatus(statusEl, 'Upload ready without optimization.');
    }
  });

  // Remember freeform tags as user types per-card tag lists.
  form.addEventListener('input', (event) => {
    if (event.target.matches('input[name="hm_taglist[]"]')) {
      safeSetLocal(LAST_TAG_TEXT_KEY, event.target.value);
    }
  });

  if (newTagInput) {
    newTagInput.addEventListener('input', saveTagsState);
  }

  if (tagCheckboxes.length) {
    tagCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', saveTagsState);
    });
  }

  // Submit via XHR to show in-page progress while keeping PHP handling intact.
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!fileInput.files || !fileInput.files.length) {
      setStatus(statusEl, 'Please choose at least one image.');
      return;
    }

    toggleSubmitting(true);
    setStatus(statusEl, 'Preparing upload...');
    setProgress(5, 'Preparing upload...');

    const formData = new FormData(form);
    const target = form.getAttribute('action') || window.location.href;
    const xhr = new XMLHttpRequest();
    xhr.open('POST', target);

    // Drive the progress bar with browser-provided upload progress events.
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        setProgress(percent, `Uploading ${percent}%`);
      } else {
        setProgress(25, 'Uploading...');
      }
    };

    // Fallback messages for network failures.
    xhr.onerror = () => {
      setStatus(statusEl, 'Upload failed. Please try again.');
      setProgress(0, '');
      toggleSubmitting(false);
    };

    // On success, follow the server redirect (includes success query params).
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 400) {
        setProgress(100, 'Finishing up...');
        const dest = xhr.responseURL || target;
        window.location.href = dest;
      } else {
        setStatus(statusEl, 'Upload failed. Please try again.');
        setProgress(0, '');
        toggleSubmitting(false);
      }
    };

    xhr.send(formData);
  });
};
